import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Shield, Users, Building, Eye, Edit, Trash2, Plus, 
  Check, X, Crown, Calculator, Wrench, Briefcase, 
  ExternalLink, UserPlus, Mail, Search, Filter
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes par rôle
const ROLE_ICONS = {
  admin: Shield,
  direction: Crown,
  commercial: Briefcase,
  technique: Wrench,
  comptable: Calculator,
  lecteur: Eye,
  externe: ExternalLink
};

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  direction: 'bg-purple-100 text-purple-700 border-purple-200',
  commercial: 'bg-blue-100 text-blue-700 border-blue-200',
  technique: 'bg-green-100 text-green-700 border-green-200',
  comptable: 'bg-amber-100 text-amber-700 border-amber-200',
  lecteur: 'bg-gray-100 text-gray-700 border-gray-200',
  externe: 'bg-slate-100 text-slate-700 border-slate-200'
};

export default function PermissionsPage() {
  const { activeSociete } = useWorkspace();
  const activeSocieteId = activeSociete?.id;
  
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [myPermissions, setMyPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  
  // États pour invitation
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('lecteur');
  
  // États pour modification
  const [editingUser, setEditingUser] = useState(null);
  const [searchUser, setSearchUser] = useState('');

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'X-Societe-Id': activeSocieteId || ''
  });

  useEffect(() => {
    if (activeSocieteId) {
      loadData();
    }
  }, [activeSocieteId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadRoles(),
        loadUsers(),
        loadMyPermissions()
      ]);
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
    setLoading(false);
  };

  const loadRoles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/workspace-roles/roles`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Erreur chargement rôles:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/workspace-roles/users`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    }
  };

  const loadMyPermissions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/workspace-roles/my-permissions`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMyPermissions(data);
      }
    } catch (error) {
      console.error('Erreur chargement permissions:', error);
    }
  };

  const inviteUser = async () => {
    if (!inviteEmail) return;
    try {
      const res = await fetch(`${API_URL}/api/workspace-roles/invite?email=${encodeURIComponent(inviteEmail)}&role_id=${inviteRole}`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        setShowInvite(false);
        setInviteEmail('');
        setInviteRole('lecteur');
        alert('Invitation envoyée !');
      }
    } catch (error) {
      console.error('Erreur invitation:', error);
    }
  };

  const updateUserRole = async (userId, roleId) => {
    try {
      const res = await fetch(`${API_URL}/api/workspace-roles/users/${userId}/role`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ role_id: roleId })
      });
      if (res.ok) {
        loadUsers();
        setEditingUser(null);
      }
    } catch (error) {
      console.error('Erreur modification rôle:', error);
    }
  };

  const removeUser = async (userId) => {
    if (!confirm('Retirer cet utilisateur du workspace ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/workspace-roles/users/${userId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (res.ok) {
        loadUsers();
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const filteredUsers = users.filter(u => 
    !searchUser || 
    (u.email || '').toLowerCase().includes(searchUser.toLowerCase()) ||
    (u.nom || '').toLowerCase().includes(searchUser.toLowerCase())
  );

  const isAdmin = myPermissions?.role_id === 'admin' || myPermissions?.full_access;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="permissions-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="h-7 w-7 text-green-600" />
          Gestion des Permissions
        </h1>
        <p className="text-gray-600">
          Gérez les accès et les rôles des utilisateurs pour {activeSociete?.nom || 'ce workspace'}
        </p>
      </div>

      {/* Mes permissions */}
      {myPermissions && (
        <Card className="mb-6 border-green-200 bg-green-50/50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${ROLE_COLORS[myPermissions.role_id] || 'bg-gray-100'}`}>
                  {React.createElement(ROLE_ICONS[myPermissions.role_id] || Eye, { className: "h-5 w-5" })}
                </div>
                <div>
                  <p className="font-medium">Votre rôle : {myPermissions.role_nom}</p>
                  <p className="text-sm text-gray-600">
                    {myPermissions.full_access ? 'Accès complet à toutes les fonctionnalités' : 
                     `${myPermissions.permissions?.length || 0} permissions actives`}
                  </p>
                </div>
              </div>
              {myPermissions.full_access && (
                <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full">Admin</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button 
          variant={activeTab === 'users' ? 'default' : 'outline'} 
          onClick={() => setActiveTab('users')}
        >
          <Users className="h-4 w-4 mr-2" />Utilisateurs ({users.length})
        </Button>
        <Button 
          variant={activeTab === 'roles' ? 'default' : 'outline'} 
          onClick={() => setActiveTab('roles')}
        >
          <Shield className="h-4 w-4 mr-2" />Rôles disponibles
        </Button>
      </div>

      {/* Tab Utilisateurs */}
      {activeTab === 'users' && (
        <div>
          {/* Actions */}
          <div className="flex justify-between items-center mb-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-lg"
              />
            </div>
            {isAdmin && (
              <Button onClick={() => setShowInvite(!showInvite)} className="bg-green-600 hover:bg-green-700">
                <UserPlus className="h-4 w-4 mr-2" />
                {showInvite ? 'Annuler' : 'Inviter un utilisateur'}
              </Button>
            )}
          </div>

          {/* Formulaire invitation */}
          {showInvite && isAdmin && (
            <Card className="mb-4 border-blue-200 bg-blue-50/50">
              <CardContent className="py-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Mail className="h-4 w-4" />Inviter un nouvel utilisateur
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <input
                      type="email"
                      placeholder="Email de l'utilisateur"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="flex-1 p-2 border rounded-lg"
                    >
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>{role.nom}</option>
                      ))}
                    </select>
                    <Button onClick={inviteUser} className="bg-blue-600 hover:bg-blue-700">
                      Envoyer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Liste utilisateurs */}
          <div className="grid gap-3">
            {filteredUsers.map(user => (
              <Card key={user.user_id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-lg font-medium text-gray-600">
                          {(user.prenom?.[0] || user.email?.[0] || '?').toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {user.prenom && user.nom ? `${user.prenom} ${user.nom}` : user.email}
                        </p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {editingUser === user.user_id ? (
                        <div className="flex items-center gap-2">
                          <select
                            defaultValue={user.role_id}
                            onChange={(e) => updateUserRole(user.user_id, e.target.value)}
                            className="p-2 border rounded-lg"
                          >
                            {roles.map(role => (
                              <option key={role.id} value={role.id}>{role.nom}</option>
                            ))}
                          </select>
                          <Button variant="ghost" size="sm" onClick={() => setEditingUser(null)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className={`px-3 py-1 rounded-full text-sm border ${ROLE_COLORS[user.role_id] || 'bg-gray-100'}`}>
                            {roles.find(r => r.id === user.role_id)?.nom || user.role_id}
                          </span>
                          {isAdmin && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => setEditingUser(user.user_id)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => removeUser(user.user_id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {filteredUsers.length === 0 && (
              <Card className="p-8 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Aucun utilisateur trouvé</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Tab Rôles */}
      {activeTab === 'roles' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {roles.map(role => {
            const Icon = ROLE_ICONS[role.id] || Shield;
            return (
              <Card key={role.id} className={`border-2 ${ROLE_COLORS[role.id]?.replace('bg-', 'border-').split(' ')[0] || 'border-gray-200'}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Icon className="h-5 w-5" />
                    {role.nom}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">{role.description}</p>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase">Permissions :</p>
                    <div className="flex flex-wrap gap-1">
                      {(role.permissions || []).slice(0, 5).map((perm, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/50 rounded text-xs">
                          {perm === '*' ? 'Toutes' : perm.split(':')[0]}
                        </span>
                      ))}
                      {(role.permissions?.length || 0) > 5 && (
                        <span className="px-2 py-0.5 bg-white/50 rounded text-xs">
                          +{role.permissions.length - 5}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
